import { User, getAuth } from 'firebase/auth';
import { PlaylistExportType } from 'hooks/usePlaylistExport';
import { MappingAutoMatchThreshold } from 'models/Mapping';
import { TrackBuyProvider } from 'models/Track';
import { UserPreferences } from 'models/UserPreferences';
import { createContext, useEffect, useState } from 'react';
import { mergeWithDefaults } from 'utils/mergeWithDefaults';

export type UserContextType = {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  safePhoto?: string;
  spotifyToken: string;
  setSpotifyToken: React.Dispatch<React.SetStateAction<string>>;
  redirectUri: string;
  setRedirectUri: React.Dispatch<React.SetStateAction<string>>;
  startSpotify: boolean;
  setStartSpotify: React.Dispatch<React.SetStateAction<boolean>>;
  manageEvent: boolean;
  setManageEvent: React.Dispatch<React.SetStateAction<boolean>>;
  requesterId: string;
  setRequesterId: React.Dispatch<React.SetStateAction<string>>;
  preferences: UserPreferences;
  setPreferences: React.Dispatch<React.SetStateAction<UserPreferences>>;
  updatePreferences: (updates: Partial<UserPreferences>) => void;
};

export const defaultPreferences: UserPreferences = {
  themeMode: 'system',
  locale: 'de',
  defaultExportType: PlaylistExportType.M3U,
  defaultBuyProvider: TrackBuyProvider.Apple,
  autoMatchThreshold: MappingAutoMatchThreshold.Medium,

  // TODO: Remove Test
  // autoMatchThresholdTest: 0.6,
  // findMatchThresholdTest: 1,

  showExportSeratoDialog: true,
  defaultRequestColumns: [],
  defaultEventListView: 'grid',
  defaultRememberRequestMapping: true,
};

export const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const auth = getAuth();
  const [safePhoto, setSafePhoto] = useState<string>();

  const [user, setUser] = useState<User | null>(() => {
    const userFromStorage = localStorage.getItem('user');
    return userFromStorage ? JSON.parse(userFromStorage) : null;
  });

  const [spotifyToken, setSpotifyToken] = useState<string>(
    () => localStorage.getItem('spotifyToken') || ''
  );

  const [redirectUri, setRedirectUri] = useState<string>(
    () => localStorage.getItem('redirectUri') || ''
  );

  const [startSpotify, setStartSpotify] = useState<boolean>(() =>
    sessionStorage.getItem('startSpotify') === 'true' ? true : false || false
  );

  const [manageEvent, setManageEvent] = useState<boolean>(() =>
    sessionStorage.getItem('manageEvent') === 'true' ? true : false || false
  );

  const [requesterId, setRequesterId] = useState<string>(
    () => localStorage.getItem('requesterId') || ''
  );

  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    const stored = localStorage.getItem('preferences');

    if (!stored) return defaultPreferences;

    try {
      const parsed = JSON.parse(stored);
      return mergeWithDefaults(defaultPreferences, parsed);
    } catch {
      return defaultPreferences;
    }
  });

  const updatePreferences = (updates: Partial<UserPreferences>) => {
    setPreferences((prev) => {
      const newPrefs = { ...prev, ...updates };
      localStorage.setItem('preferences', JSON.stringify(newPrefs));
      return newPrefs;
    });
  };

  // Save the values in LocalStorage if they change
  useEffect(() => {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('spotifyToken', spotifyToken);
    localStorage.setItem('redirectUri', redirectUri);
    sessionStorage.setItem('startSpotify', JSON.stringify(startSpotify));
    sessionStorage.setItem('manageEvent', JSON.stringify(manageEvent));
    localStorage.setItem('requesterId', requesterId);
    localStorage.setItem('preferences', JSON.stringify(preferences));
  }, [user, spotifyToken, redirectUri, startSpotify, manageEvent, requesterId, preferences]);

  useEffect(() => {
    auth.onAuthStateChanged((newUser) => {
      setUser(newUser);
    });
  }, []);

  useEffect(() => {
    async function fetchPhoto() {
      if (!user?.photoURL) return;
      try {
        const res = await fetch(user.photoURL, { credentials: 'include' });
        const blob = await res.blob();
        setSafePhoto(URL.createObjectURL(blob));
      } catch {
        setSafePhoto(undefined);
      }
    }
    fetchPhoto();
  }, [user?.photoURL]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        safePhoto,
        spotifyToken,
        setSpotifyToken,
        redirectUri,
        setRedirectUri,
        startSpotify,
        setStartSpotify,
        manageEvent,
        setManageEvent,
        requesterId,
        setRequesterId,
        preferences,
        setPreferences,
        updatePreferences,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
